import { useStore } from "@nanostores/solid";
import { createFileRoute } from "@tanstack/solid-router";
import { For, Show, onCleanup, onMount } from "solid-js";
import {
  IconArrowRight,
  IconBuilding2,
  IconCheck,
  IconCircleCheck,
  IconCircleDollarSign,
  IconCloud,
  IconCloudUpload,
  IconCpu,
  IconFiles,
  IconHandshake,
  IconLandmark,
  IconListTree,
  IconMessageCircle,
  IconPencil,
  IconScanText,
  IconSheet,
  IconSparkles,
  IconTruck,
  IconUser,
} from "~/components/icons";
import {
  AiExtractMockup,
  BackupsMockup,
  ChartOfAccountsMockup,
  DepreciationMockup,
  DocumentExtractionMockup,
  JournalEntryMockup,
  OcrMockup,
  PostLedgerMockup,
  ReviewMockup,
  UploadMockup,
  VendorMatchMockup,
} from "~/components/landing";
import { Button } from "~/components/ui/button";
import { authClient } from "~/lib/auth-client";

export const Route = createFileRoute("/_main/")({
  // Preload the hero LCP image so the browser discovers it from the <head>
  // instead of waiting to parse the <img> tag in the body. Scoped to the
  // landing page only — the root layout must NOT preload it on dashboard
  // pages (wasted bandwidth + console warning).
  //
  // Only the dark variant is preloaded (unconditional): the hero stage
  // renders the dark screenshot in every color scheme, so the media-gated
  // light preload was dead weight — it fired in light mode but was never
  // consumed ("preloaded but not used" console warning).
  head: () => ({
    links: [
      {
        rel: "preload",
        as: "image",
        href: "/dashboard-lg-dark.webp?v=6",
        fetchpriority: "high",
        imagesrcset:
          "/dashboard-640-dark.webp?v=6 640w, /dashboard-1024-dark.webp?v=6 1024w, /dashboard-1216-dark.webp?v=6 1216w, /dashboard-1536-dark.webp?v=6 1536w, /dashboard-lg-dark.webp?v=6 2432w",
        imagesizes: "(min-width: 1280px) 1216px, calc(100vw - 2rem)",
      },
    ],
  }),
  component: Home,
});

const deploymentFeatures = [
  {
    label: "Multi-Business Workspaces",
    icon: IconBuilding2,
    desc: "Manage multiple entities under one account with isolated data and shared workflows.",
    accent: "indigo",
  },
  {
    label: "Chart of Accounts",
    icon: IconListTree,
    desc: "Model your books with a clear account hierarchy that supports day-to-day entries and reporting.",
    accent: "violet",
  },
  {
    label: "AI-Powered Extraction",
    icon: IconFiles,
    desc: "Ingest invoices and receipts with OCR and AI. Convert raw documents into structured data.",
    accent: "blue",
  },
  {
    label: "Journal Entry Tracking",
    icon: IconCircleDollarSign,
    desc: "Capture journal activity with consistent categorization and audit-friendly structure.",
    accent: "emerald",
  },
  {
    label: "Worksheets & Fixed Assets",
    icon: IconSheet,
    desc: "Run depreciation schedules, worksheets, and asset tracking alongside your books.",
    accent: "amber",
  },
  {
    label: "AI Assistant & Chat",
    icon: IconMessageCircle,
    desc: "Built-in assistant tooling and Telegram integration to speed up repetitive tasks.",
    accent: "rose",
  },
];

const accentMap: Record<string, { chip: string; glow: string }> = {
  indigo: {
    chip: "bg-indigo-500/10 text-indigo-300 ring-indigo-500/20 group-hover:bg-indigo-500/20",
    glow: "group-hover:shadow-[0_0_0_1px_rgba(129,140,248,0.15)_inset,0_20px_50px_-25px_rgba(129,140,248,0.45)]",
  },
  violet: {
    chip: "bg-violet-500/10 text-violet-300 ring-violet-500/20 group-hover:bg-violet-500/20",
    glow: "group-hover:shadow-[0_0_0_1px_rgba(167,139,250,0.15)_inset,0_20px_50px_-25px_rgba(167,139,250,0.45)]",
  },
  blue: {
    chip: "bg-blue-500/10 text-blue-300 ring-blue-500/20 group-hover:bg-blue-500/20",
    glow: "group-hover:shadow-[0_0_0_1px_rgba(96,165,250,0.15)_inset,0_20px_50px_-25px_rgba(96,165,250,0.45)]",
  },
  emerald: {
    chip: "bg-emerald-500/10 text-emerald-300 ring-emerald-500/20 group-hover:bg-emerald-500/20",
    glow: "group-hover:shadow-[0_0_0_1px_rgba(52,211,153,0.15)_inset,0_20px_50px_-25px_rgba(52,211,153,0.45)]",
  },
  amber: {
    chip: "bg-amber-500/10 text-amber-300 ring-amber-500/20 group-hover:bg-amber-500/20",
    glow: "group-hover:shadow-[0_0_0_1px_rgba(251,191,36,0.15)_inset,0_20px_50px_-25px_rgba(251,191,36,0.45)]",
  },
  rose: {
    chip: "bg-rose-500/10 text-rose-300 ring-rose-500/20 group-hover:bg-rose-500/20",
    glow: "group-hover:shadow-[0_0_0_1px_rgba(251,113,133,0.15)_inset,0_20px_50px_-25px_rgba(251,113,133,0.45)]",
  },
};

const heroFeatures = [
  { label: "Multi-Business Workspaces", icon: IconBuilding2 },
  { label: "Chart of Accounts", icon: IconListTree },
  { label: "Journal Transactions", icon: IconCircleDollarSign },
  { label: "Documents and OCR", icon: IconFiles },
  { label: "Worksheets", icon: IconSheet },
  { label: "Fixed Assets", icon: IconTruck },
  { label: "Vendor Management", icon: IconHandshake },
  { label: "AI Assistant and Telegram", icon: IconMessageCircle },
];

// The real document → ledger pipeline, grounded in the product's workflow
// engine (OCR → AI extraction → vendor match → journal entry).
const flowSteps = [
  {
    label: "Upload",
    desc: "Drop in an invoice, receipt, or bank statement.",
    icon: IconCloudUpload,
    accent: "text-indigo-300 bg-indigo-500/15 ring-indigo-500/25",
  },
  {
    label: "OCR",
    desc: "Text is pulled from the document automatically.",
    icon: IconScanText,
    accent: "text-blue-300 bg-blue-500/15 ring-blue-500/25",
  },
  {
    label: "AI Extract",
    desc: "Vendor, amount, date, and type are identified.",
    icon: IconCpu,
    accent: "text-violet-300 bg-violet-500/15 ring-violet-500/25",
  },
  {
    label: "Vendor Match",
    desc: "Matched to an existing vendor or created for you.",
    icon: IconUser,
    accent: "text-amber-300 bg-amber-500/15 ring-amber-500/25",
  },
  {
    label: "Review",
    desc: "Approve the suggested entry before it posts.",
    icon: IconPencil,
    accent: "text-emerald-300 bg-emerald-500/15 ring-emerald-500/25",
  },
  {
    label: "Post to Ledger",
    desc: "A balanced journal entry lands in your books.",
    icon: IconLandmark,
    accent: "text-rose-300 bg-rose-500/15 ring-rose-500/25",
  },
];

function Eyebrow(props: { children: string }) {
  return (
    <div class="mb-4 inline-flex items-center gap-2">
      <span class="h-1 w-1 rounded-full bg-indigo-400" />
      <span class="text-xs font-semibold uppercase tracking-[0.22em] text-current opacity-50">
        {props.children}
      </span>
    </div>
  );
}

function Home() {
  const session = useStore(authClient.useSession);

  let videoRef: HTMLVideoElement | undefined;
  let timelineRef: HTMLOListElement | undefined;
  let io: IntersectionObserver | undefined;

  onMount(() => {
    videoRef?.play().catch(() => {});
    if (timelineRef) {
      io = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            timelineRef!.dataset.play = "true";
            io?.disconnect();
          }
        },
        { rootMargin: "0px 0px -20% 0px" },
      );
      io.observe(timelineRef);
    }
  });

  onCleanup(() => {
    io?.disconnect();
    if (videoRef) {
      videoRef.pause();
      videoRef.removeAttribute("src");
      videoRef.load();
    }
  });

  return (
    <>
      <main class="flex flex-col selection:bg-indigo-500/30 selection:text-white">
        {/* ----------------------------------------------------------------
          HERO
          ---------------------------------------------------------------- */}
        <section class="hero relative overflow-hidden bg-[#050308] min-h-100 pb-24 pt-24 md:min-h-175 md:pb-20 md:pt-16 lg:min-h-225 lg:pb-28 lg:pt-20 xl:min-h-[950px] xl:pt-24 sm:pb-16">
          {/* Content */}
          <div class="container relative z-10 md:mx-auto flex max-w-7xl flex-col px-4 sm:px-8">
            {/* Eyebrow */}
            <div class="relative z-30 mb-5 hidden md:inline-flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 backdrop-blur-sm">
              <span class="h-1.5 w-1.5 rounded-full bg-indigo-400 shadow-[0_0_8px_2px_rgba(129,140,248,0.6)]" />
              <span class="text-[11px] font-medium uppercase tracking-[0.18em] text-white/70">
                Built for modern books
              </span>
            </div>

            {/* Heading */}
            <h1 class="relative z-30 max-w-52 bg-linear-to-br from-white from-30% via-[#d5d8f6] via-80% to-[#fdf7fe] bg-clip-text text-3xl font-semibold leading-[1.05] tracking-tight text-transparent md:max-w-[441px] md:text-5xl lg:max-w-[560px] lg:text-7xl xl:max-w-[640px]">
              Accounting
              <br />
              without the friction.
            </h1>

            {/* Subtitle */}
            <p class="relative z-30 mt-4 max-w-44 text-sm leading-snug tracking-tight text-white/70 md:mt-6 md:max-w-md md:text-lg">
              Modern accounting with AI extraction, multi-business workspaces,
              and audit-ready books.
            </p>

            {/* CTA */}
            <div class="mt-6 md:mt-12">
              <div class="relative inline-flex items-center gap-3">
                <Show
                  when={session().data}
                  fallback={
                    <Button
                      as="a"
                      href="/login"
                      size="lg"
                      class="rounded-full bg-white px-14 text-gray-900 shadow-[0_8px_30px_-8px_rgba(129,140,248,0.55)] transition-all duration-200 hover:bg-white/90 hover:shadow-[0_10px_36px_-6px_rgba(129,140,248,0.7)] hover:-translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-400"
                    >
                      Get Started
                    </Button>
                  }
                >
                  <Button
                    as="a"
                    href="/dashboard"
                    size="lg"
                    class="rounded-full bg-white px-14 text-gray-900 shadow-[0_8px_30px_-8px_rgba(129,140,248,0.55)] transition-all duration-200 hover:bg-white/90 hover:shadow-[0_10px_36px_-6px_rgba(129,140,248,0.7)] hover:-translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-400"
                  >
                    Dashboard
                  </Button>
                </Show>

                <Button
                  as="a"
                  href="#features"
                  size="lg"
                  variant="outline"
                  class="hidden rounded-full border-white/15 bg-white/5 text-white transition-all duration-200 hover:border-white/25 hover:bg-white/10 hover:text-white md:inline-flex"
                >
                  Explore Features
                </Button>
              </div>
            </div>

            {/* DASHBOARD STAGE */}
            <div class="relative mt-[60px] flex flex-col md:mt-[80px] lg:mt-[100px] xl:mt-[120px]">
              <div
                class="pointer-events-none absolute left-1/2 bottom-full z-[-1] w-[750px] md:w-[1000px] lg:w-[750px] -translate-x-[46%] md:-translate-x-[35%] lg:-translate-x-[30%] xl:-translate-x-[30%] translate-y-[100px] md:translate-y-[135px] lg:translate-y-[215px] scale-[0.95] lg:scale-[2.05] origin-bottom"
                aria-hidden="true"
              >
                <video
                  ref={videoRef}
                  src="/hero.mp4"
                  autoplay
                  loop
                  muted
                  playsinline
                  width={1152}
                  height={918}
                  class="h-auto w-full mix-blend-lighten opacity-90"
                >
                  <track kind="captions" />
                </video>
              </div>
              <div class="stage relative z-10 w-full">
                <div class="relative overflow-hidden rounded-2xl bg-[#050308] ring-1 ring-white/10 shadow-[0_0_80px_20px_rgba(129,140,248,0.22),0_30px_90px_-30px_rgba(0,0,0,0.8),0_0_0_1px_rgba(129,140,248,0.12)_inset]">
                  <div class="w-full">
                    <img
                      fetchpriority="high"
                      decoding="async"
                      src="/dashboard-lg-dark.webp?v=6"
                      srcset="
                        /dashboard-640-dark.webp?v=6 640w,
                        /dashboard-1024-dark.webp?v=6 1024w,
                        /dashboard-1216-dark.webp?v=6 1216w,
                        /dashboard-1536-dark.webp?v=6 1536w,
                        /dashboard-lg-dark.webp?v=6 2432w
                      "
                      sizes="(min-width: 1280px) 1216px, calc(100vw - 2rem)"
                      width="1216"
                      height="734"
                      alt="Dashboard preview"
                      class="h-full w-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature carousel */}
          <div class="relative z-20 mt-10 sm:mt-10 md:mt-12 lg:mt-16">
            <div class="mx-auto max-w-7xl px-8">
              <p class="mb-4 text-xs font-medium uppercase tracking-[0.2em] text-white/50">
                Built for daily accounting operations
              </p>
              <div
                class="hero-feature-carousel w-full"
                style="mask-image: linear-gradient(to right, transparent, black 8%, black 92%, transparent); -webkit-mask-image: linear-gradient(to right, transparent, black 8%, black 92%, transparent);"
              >
                <div class="hero-feature-track">
                  <For each={[...heroFeatures, ...heroFeatures]}>
                    {(feature) => (
                      <div class="hero-feature-item text-white/70 transition-colors duration-200 hover:text-white">
                        <feature.icon class="h-4 w-4" />
                        <span>{feature.label}</span>
                      </div>
                    )}
                  </For>
                </div>
              </div>
            </div>
          </div>

          {/* Impact glow */}
          <div class="pointer-events-none absolute inset-x-0 top-[420px] z-10 h-[900px]">
            <div class="relative mx-auto h-full max-w-6xl px-8">
              <div
                class="absolute inset-0 rounded-full opacity-30 blur-3xl"
                style="background: radial-gradient(circle at 50% 30%, rgba(99,102,241,0.22), transparent 60%);"
              />
            </div>
          </div>
          {/* Fade overlay */}
          <div class="pointer-events-none absolute bottom-0 left-0 right-0 h-72 bg-linear-to-b from-transparent to-[#050308]" />
        </section>

        {/* ----------------------------------------------------------------
          FEATURES SHOWCASE
          ---------------------------------------------------------------- */}
        <div class="overflow-hidden bg-[#050308]">
          <section id="features" class="relative z-10">
            <div class="relative z-10 mx-auto max-w-7xl px-8 text-white">
              <Eyebrow>Platform</Eyebrow>
              <h2 class="text-5xl font-bold leading-[1.05] tracking-tighter text-white sm:text-4xl lg:text-7xl">
                Built for precision
                <br />
                across your operations
              </h2>
              <p class="mt-6 max-w-2xl text-lg leading-tight tracking-tight text-white/60 lg:mt-5 sm:mt-3 sm:max-w-lg sm:text-sm">
                From chart of accounts to fixed asset depreciation, every
                feature is designed to reduce friction and keep your books
                audit-ready.
              </p>

              <div class="mt-16 space-y-24 lg:mt-20 lg:space-y-32">
                {/* ── Feature 1: Documents & AI Extraction ── */}
                <div class="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
                  <div>
                    <div class="mb-4 inline-flex items-center gap-2 rounded-full bg-indigo-500/10 px-3 py-1 ring-1 ring-indigo-500/20">
                      <IconFiles class="h-3.5 w-3.5 text-indigo-300" />
                      <span class="text-xs font-medium text-indigo-200">
                        Documents &amp; AI Extraction
                      </span>
                    </div>
                    <h3 class="text-3xl font-bold leading-[1.05] tracking-tighter text-white lg:text-4xl">
                      From paper to
                      <br />
                      structured data.
                    </h3>
                    <p class="mt-4 max-w-md text-base leading-snug tracking-tight text-white/60">
                      Drop in an invoice, receipt, or bank statement. OCR pulls
                      the text, then AI identifies the vendor, amount, date, and
                      document type — ready to review and post.
                    </p>
                    <ul class="mt-6 space-y-3">
                      <li class="flex items-start gap-3">
                        <IconCheck class="mt-0.5 h-4 w-4 shrink-0 text-indigo-400" />
                        <span class="text-sm text-white/70">
                          Supports bills, invoices, receipts, statements, and
                          more
                        </span>
                      </li>
                      <li class="flex items-start gap-3">
                        <IconCheck class="mt-0.5 h-4 w-4 shrink-0 text-indigo-400" />
                        <span class="text-sm text-white/70">
                          Vendor, amount, date, and type extracted automatically
                        </span>
                      </li>
                      <li class="flex items-start gap-3">
                        <IconCheck class="mt-0.5 h-4 w-4 shrink-0 text-indigo-400" />
                        <span class="text-sm text-white/70">
                          Review before anything touches your books
                        </span>
                      </li>
                    </ul>
                  </div>

                  {/* Mockup: document extraction */}
                  <DocumentExtractionMockup />
                </div>

                {/* ── Feature 2: Journal Entries ── */}
                <div class="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
                  {/* Mockup: ledger */}
                  <div class="order-2 lg:order-1">
                    <JournalEntryMockup />
                  </div>

                  <div class="order-1 lg:order-2">
                    <div class="mb-4 inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-3 py-1 ring-1 ring-emerald-500/20">
                      <IconCircleDollarSign class="h-3.5 w-3.5 text-emerald-300" />
                      <span class="text-xs font-medium text-emerald-200">
                        Journal Entries
                      </span>
                    </div>
                    <h3 class="text-3xl font-bold leading-[1.05] tracking-tighter text-white lg:text-4xl">
                      Balanced books,
                      <br />
                      every time.
                    </h3>
                    <p class="mt-4 max-w-md text-base leading-snug tracking-tight text-white/60">
                      Journal entries are validated for balance and categorized
                      consistently, with documents linked right to the lines
                      that support them.
                    </p>
                    <ul class="mt-6 space-y-3">
                      <li class="flex items-start gap-3">
                        <IconCheck class="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                        <span class="text-sm text-white/70">
                          Debits and credits validated before posting
                        </span>
                      </li>
                      <li class="flex items-start gap-3">
                        <IconCheck class="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                        <span class="text-sm text-white/70">
                          Link source documents to supporting lines
                        </span>
                      </li>
                      <li class="flex items-start gap-3">
                        <IconCheck class="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                        <span class="text-sm text-white/70">
                          Audit-friendly structure across accounts and periods
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* ── Feature 3: Chart of Accounts ── */}
                <div class="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
                  <div>
                    <div class="mb-4 inline-flex items-center gap-2 rounded-full bg-violet-500/10 px-3 py-1 ring-1 ring-violet-500/20">
                      <IconListTree class="h-3.5 w-3.5 text-violet-300" />
                      <span class="text-xs font-medium text-violet-200">
                        Chart of Accounts
                      </span>
                    </div>
                    <h3 class="text-3xl font-bold leading-[1.05] tracking-tighter text-white lg:text-4xl">
                      A hierarchy that
                      <br />
                      makes sense.
                    </h3>
                    <p class="mt-4 max-w-md text-base leading-snug tracking-tight text-white/60">
                      Model your books with a clear account hierarchy that
                      supports day-to-day entries and long-term reporting.
                    </p>
                    <ul class="mt-6 space-y-3">
                      <li class="flex items-start gap-3">
                        <IconCheck class="mt-0.5 h-4 w-4 shrink-0 text-violet-400" />
                        <span class="text-sm text-white/70">
                          Parent–child account structure with rollups
                        </span>
                      </li>
                      <li class="flex items-start gap-3">
                        <IconCheck class="mt-0.5 h-4 w-4 shrink-0 text-violet-400" />
                        <span class="text-sm text-white/70">
                          Consistent categorization across every entry
                        </span>
                      </li>
                      <li class="flex items-start gap-3">
                        <IconCheck class="mt-0.5 h-4 w-4 shrink-0 text-violet-400" />
                        <span class="text-sm text-white/70">
                          Built for reporting, not just bookkeeping
                        </span>
                      </li>
                    </ul>
                  </div>

                  {/* Mockup: account tree */}
                  <ChartOfAccountsMockup />
                </div>

                {/* ── Feature 4: Worksheets & Fixed Assets ── */}
                <div class="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
                  {/* Mockup: depreciation schedule */}
                  <div class="order-2 lg:order-1">
                    <DepreciationMockup />
                  </div>

                  <div class="order-1 lg:order-2">
                    <div class="mb-4 inline-flex items-center gap-2 rounded-full bg-blue-500/10 px-3 py-1 ring-1 ring-blue-500/20">
                      <IconSheet class="h-3.5 w-3.5 text-blue-300" />
                      <span class="text-xs font-medium text-blue-200">
                        Worksheets &amp; Fixed Assets
                      </span>
                    </div>
                    <h3 class="text-3xl font-bold leading-[1.05] tracking-tighter text-white lg:text-4xl">
                      Depreciation without
                      <br />
                      the spreadsheet sprawl.
                    </h3>
                    <p class="mt-4 max-w-md text-base leading-snug tracking-tight text-white/60">
                      Run depreciation schedules, worksheets, and asset tracking
                      alongside your books — no more juggling separate
                      spreadsheets.
                    </p>
                    <ul class="mt-6 space-y-3">
                      <li class="flex items-start gap-3">
                        <IconCheck class="mt-0.5 h-4 w-4 shrink-0 text-blue-400" />
                        <span class="text-sm text-white/70">
                          Straight-line and custom depreciation schedules
                        </span>
                      </li>
                      <li class="flex items-start gap-3">
                        <IconCheck class="mt-0.5 h-4 w-4 shrink-0 text-blue-400" />
                        <span class="text-sm text-white/70">
                          Asset tracking alongside your books
                        </span>
                      </li>
                      <li class="flex items-start gap-3">
                        <IconCheck class="mt-0.5 h-4 w-4 shrink-0 text-blue-400" />
                        <span class="text-sm text-white/70">
                          Worksheets that stay in sync with the ledger
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ----------------------------------------------------------------
            How it works — user flow pipeline
            ---------------------------------------------------------------- */}
          <section class="relative overflow-hidden py-24 lg:py-28 md:py-20 sm:py-16">
            <div class="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-white/10 to-transparent" />
            <div class="mx-auto max-w-7xl px-8 text-white">
              <div>
                <Eyebrow>How it works</Eyebrow>
                <h2 class="max-w-2xl text-5xl font-bold leading-[1.05] tracking-tighter text-white lg:text-6xl md:max-w-xl md:text-5xl sm:max-w-lg sm:text-4xl">
                  From document
                  <br />
                  to ledger in one flow.
                </h2>
                <p class="mt-5 max-w-xl text-base leading-snug tracking-tight text-white/60">
                  Upload a document and watch it move through OCR, AI
                  extraction, vendor matching, and review — then post a balanced
                  journal entry without re-keying a single field.
                </p>
              </div>

              {/* Timeline — all large step cards visible, alternating left/right */}
              <ol ref={timelineRef} class="flow-timeline mt-16 lg:mt-24">
                <For each={flowSteps}>
                  {(step, i) => {
                    const even = i() % 2 === 0;
                    return (
                      <li class="flow-row" style={{ "--delay": `${i() * 2}s` }}>
                        {/* Node on the connecting line */}
                        <div class="flow-node">
                          <div class="flow-step-glow" aria-hidden="true" />
                          <div
                            class={`flow-step-icon relative flex h-12 w-12 items-center justify-center rounded-2xl ring-1 ${step.accent}`}
                          >
                            <step.icon class="h-6 w-6" />
                          </div>
                          <span class="flow-step-check" aria-hidden="true">
                            <IconCheck class="h-3 w-3" />
                          </span>
                        </div>

                        {/* Label cell */}
                        <div
                          class={`flow-cell ${even ? "lg:order-1 lg:text-right" : "lg:order-2 lg:text-left"}`}
                        >
                          <div
                            class={`flex items-center gap-2 ${even ? "lg:flex-row-reverse" : ""}`}
                          >
                            <span class="text-xs font-semibold uppercase tracking-[0.2em] text-white/40">
                              Step {i() + 1}
                            </span>
                            <span class="h-px w-8 bg-white/15" />
                          </div>
                          <h3 class="mt-3 text-2xl font-bold leading-tight tracking-tight text-white lg:text-3xl">
                            {step.label}
                          </h3>
                          <p class="mt-2 text-sm leading-snug tracking-tight text-white/55 lg:text-base">
                            {step.desc}
                          </p>
                        </div>

                        {/* Card cell */}
                        <div
                          class={`flow-card flow-cell ${even ? "lg:order-2" : "lg:order-1"}`}
                        >
                          <Show when={i() === 0}>
                            <UploadMockup />
                          </Show>
                          <Show when={i() === 1}>
                            <OcrMockup />
                          </Show>
                          <Show when={i() === 2}>
                            <AiExtractMockup />
                          </Show>
                          <Show when={i() === 3}>
                            <VendorMatchMockup />
                          </Show>
                          <Show when={i() === 4}>
                            <ReviewMockup />
                          </Show>
                          <Show when={i() === 5}>
                            <PostLedgerMockup />
                          </Show>
                        </div>
                      </li>
                    );
                  }}
                </For>
              </ol>
            </div>
          </section>

          {/* ----------------------------------------------------------------
            Cloud deployment
            ---------------------------------------------------------------- */}
          <section class="py-24 lg:py-28 md:py-20 sm:py-16">
            <div class="mx-auto max-w-7xl px-8">
              <div class="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
                {/* Left column */}
                <div class="flex flex-col text-white">
                  <Eyebrow>Infrastructure</Eyebrow>
                  <h2 class="relative z-10 text-4xl font-bold leading-[1.05] tracking-tighter sm:text-5xl lg:text-6xl">
                    Cloud deployment
                    <br />
                    built for teams.
                  </h2>
                  <p class="relative z-10 mt-5 max-w-xl text-lg leading-snug tracking-tight text-white/60 sm:mt-4 md:text-base">
                    Run the same accounting workflows for every team member with
                    secure access, automatic backups, and fully managed
                    infrastructure � no ops required.
                  </p>

                  <p class="relative z-10 mt-10 max-w-xl text-lg leading-snug tracking-tight text-white/60 lg:mt-8 sm:mt-8 md:text-base">
                    Work across multiple businesses with confidence. Every
                    feature is built for collaboration, data integrity, and
                    audit readiness.
                  </p>

                  <ul class="relative z-10 mt-8 flex flex-col gap-7 sm:mt-8 sm:gap-6">
                    <li class="group flex items-start gap-4">
                      <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-500/15 transition-transform duration-200 group-hover:scale-105 sm:h-9 sm:w-9">
                        <IconCloud class="h-5 w-5 sm:h-4 sm:w-4" />
                      </div>
                      <div>
                        <h3 class="text-lg font-bold leading-tight tracking-tight sm:text-base">
                          Automatic backups
                        </h3>
                        <p class="mt-1 max-w-md text-sm font-light leading-snug tracking-tight text-white/60">
                          Never lose a transaction. Daily encrypted backups with
                          point-in-time recovery.
                        </p>
                      </div>
                    </li>
                    <li class="group flex items-start gap-4">
                      <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-blue-400 ring-1 ring-blue-500/15 transition-transform duration-200 group-hover:scale-105 sm:h-9 sm:w-9">
                        <IconCircleCheck class="h-5 w-5 sm:h-4 sm:w-4" />
                      </div>
                      <div>
                        <h3 class="text-lg font-bold leading-tight tracking-tight sm:text-base">
                          Audit-ready
                        </h3>
                        <p class="mt-1 max-w-md text-sm font-light leading-snug tracking-tight text-white/60">
                          Full transaction history with consistent
                          categorization and immutable records.
                        </p>
                      </div>
                    </li>
                    <li class="group flex items-start gap-4">
                      <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-500/10 text-indigo-400 ring-1 ring-indigo-500/15 transition-transform duration-200 group-hover:scale-105 sm:h-9 sm:w-9">
                        <IconBuilding2 class="h-5 w-5 sm:h-4 sm:w-4" />
                      </div>
                      <div>
                        <h3 class="text-lg font-bold leading-tight tracking-tight sm:text-base">
                          Multi-business
                        </h3>
                        <p class="mt-1 max-w-md text-sm font-light leading-snug tracking-tight text-white/60">
                          Manage multiple entities under one account with
                          isolated data and shared workflows.
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>

                {/* Right column — backups & security mockup */}
                <BackupsMockup />
              </div>
            </div>
          </section>
        </div>

        {/* ----------------------------------------------------------------
          CTA
          ---------------------------------------------------------------- */}
        <section class="relative overflow-hidden bg-[#050308] pb-48 pt-40 text-white lg:pb-44 lg:pt-36 md:pb-32 md:pt-28 sm:relative sm:pb-40 sm:pt-32">
          <div class="relative z-10 mx-auto max-w-7xl px-8">
            {/* Decorative glow */}
            <div class="pointer-events-none absolute -top-40 left-0 right-0 h-[520px] lg:-top-32">
              <div
                class="absolute left-0 top-0 h-full w-full max-w-2xl opacity-40 blur-3xl"
                style="background: radial-gradient(circle at 20% 30%, rgba(99,102,241,0.28), transparent 60%);"
              />
            </div>
            <div class="relative z-10 max-w-xl">
              <Eyebrow>Get started</Eyebrow>
              <h2 class="max-w-2xl bg-linear-to-br from-white from-30% via-[#d5d8f6] via-80% to-[#fdf7fe] bg-clip-text text-5xl font-bold leading-[1.05] tracking-tight text-transparent lg:text-7xl md:max-w-xl md:text-5xl sm:max-w-lg sm:text-4xl">
                Ready to run your accounting stack your way?
              </h2>
              <p class="relative z-10 mb-8 mt-3 text-base leading-snug tracking-tight text-white/60 lg:mb-6 md:mb-5">
                Build on a platform that covers transactions, documents,
                worksheets, fixed assets, and AI-powered workflows in one place.
              </p>

              <div class="relative z-0 flex h-fit w-fit flex-col items-start gap-4 md:flex-row md:items-center">
                <Show
                  when={session().data}
                  fallback={
                    <Button
                      as="a"
                      href="/login"
                      size="lg"
                      class="relative z-10 gap-2 overflow-hidden rounded-full bg-white px-14 text-xs font-bold uppercase tracking-[-0.015em] text-[#0b0a12] shadow-[0_8px_30px_-8px_rgba(255,255,255,0.35)] transition-all duration-200 hover:-translate-y-px hover:bg-white/90 hover:shadow-[0_10px_36px_-6px_rgba(255,255,255,0.45)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-400"
                    >
                      <span class="relative z-10">Sign In</span>
                      <IconArrowRight class="relative z-10 h-[9px] w-[17px]" />
                    </Button>
                  }
                >
                  <Button
                    as="a"
                    href="/dashboard"
                    size="lg"
                    class="relative z-10 gap-2 overflow-hidden rounded-full bg-white px-14 text-xs font-bold uppercase tracking-[-0.015em] text-[#0b0a12] shadow-[0_8px_30px_-8px_rgba(255,255,255,0.35)] transition-all duration-200 hover:-translate-y-px hover:bg-white/90 hover:shadow-[0_10px_36px_-6px_rgba(255,255,255,0.45)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-400"
                  >
                    <span class="relative z-10">Dashboard</span>
                    <IconArrowRight class="relative z-10 h-[9px] w-[17px]" />
                  </Button>
                </Show>
                <Button
                  as="a"
                  href="#features"
                  size="lg"
                  variant="outline"
                  class="relative flex w-[174px] items-center gap-1.5 rounded-full border border-white/10 bg-white/5 pl-5 pr-6 text-xs font-bold uppercase tracking-tight text-white transition-all duration-200 hover:border-white/20 hover:bg-white/10 hover:text-white"
                >
                  <IconSparkles class="z-10 h-4 w-4" />
                  <span class="z-10">Learn More</span>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
